import {
    FormContext,
    FormDispatchers,
    FormSchema,
    FormState,
    FormValues,
    PageActionType,
    PaginatedComponents,
    PaginatedElement,
    ValueActionType
} from "./types";
import {
    evaluates_as_hiddenV2,
    evaluates_as_visibleV2
} from "./conditional-logic";

import { NodeBuilderFlags } from "typescript";
import { Page } from "./controls";
import React from "react";
import createSchema from "./form-schema";
import createSubmission from "./submission";
import { validate } from "./validation";

/**
 * Create Form Context
 * @param schema
 * @param state
 */
const createFormContext = (
    schema: Partial<FormSchema>,
    state: FormState & FormDispatchers
): FormContext => {
    const form = createSchema(schema);

    return Object.assign(
        { form, state },
        conditionalLogicBehaviours(state),
        pagingBehaviours(state),
        valueBehaviours(state),
        validationBehaviours(state)
    );
};

const getControlProps = (controls: React.ReactNode[]) => {
    const { props } = React.Children.toArray(controls).reduce(
        (
            {
                section,
                props
            }: {
                section: { [key: string]: any };
                props: { [key: string]: any };
            },
            node
        ) => {
            if (React.isValidElement(node) && node.props.name) {
                if (node.props.type === "section") {
                    return {
                        section: node.props,
                        props: { ...props, [node.props.name]: node.props }
                    };
                } else if (node.props.type === "page") {
                    return {
                        section: {},
                        props: { ...props, [node.props.name]: node.props }
                    };
                } else {
                    return {
                        section,
                        props: {
                            ...props,
                            [node.props.name]: { ...node.props, section }
                        }
                    };
                }
            } else {
                return { section, props };
            }
        },
        { section: {}, props: {} }
    );
    return props;
};

/**
 * Create value behaviours
 * @param state
 */
const conditionalLogicBehaviours = (state: FormState) => {
    return Object.assign({}, canGetConditionalLogicState(state));
};

/**
 * Create conditional logic behaviours
 * @param state
 */
const canGetConditionalLogicState = (state: FormState) => {
    return {
        /**
         * Evaluate condition logic for controls
         * @param controls
         */
        isHidden: (name: string) => {
            const control = state.controlProps[name];
            if (!control) return false;

            const isFormModuleHidden = checkFormModuleConditionalLogic(control, state, false);
            const isControlHidden = evaluates_as_hiddenV2(control.conditionalLogic, state);
            if (control.type !== "page") {
                const page = (state.controls as React.ReactElement[]).find(targetControl =>
                    targetControl.props.type === "page" && targetControl.props.pageNumber == control.pageNumber);
                const isPageHidden = !!page && evaluates_as_hiddenV2(page.props.conditionalLogic, state);
                
                return isPageHidden || isControlHidden || isFormModuleHidden;
            } else {
                return isControlHidden || isFormModuleHidden;
            }
        },
        isVisible: (name: string) => {
            const control = state.controlProps[name];
            if (!control) return true;

            const isFormModuleVisible = checkFormModuleConditionalLogic(control, state, true);
            const isControlVisible = evaluates_as_visibleV2(control.conditionalLogic, state);
            if (control.type !== "page") {
                const page = (state.controls as React.ReactElement[]).find(targetControl =>
                    targetControl.props.type === "page" && targetControl.props.pageNumber == control.pageNumber);
                const isPageVisible = !page || evaluates_as_visibleV2(page.props.conditionalLogic, state);
                return isPageVisible && isControlVisible && isFormModuleVisible;
            } else {
                return isControlVisible && isFormModuleVisible;
            }
        }
    };
};

/**
 * Create value behaviours
 * @param state
 */
const valueBehaviours = (state: FormState & FormDispatchers) => {
    return Object.assign({}, canGetValues(state), canSetValues(state));
};

/**
 * Get Form Values (values of controls hidden by conditional logic are not included)
 * @param state
 */
const getValues = (state: FormState) => {
    const cl = canGetConditionalLogicState(state);
    return new Proxy(state.values, {
        get(values, name) {
            return typeof name == "string" && cl.isVisible(name)
                ? values[name]
                : undefined;
        }
    });
};

/**
 * Create value getter behaviours
 * @param state
 */
const canGetValues = (state: FormState) => {
    let values: FormValues;
    return {
        /**
         * Get control
         */
        getControl: (name: string) => state.controlProps[name],
        /**
         * Get control value by id
         * @param id
         */
        getValue: (name: string, defaultValue = undefined) =>
            state.values[name] !== undefined
                ? state.values[name]
                : defaultValue,
        /**
         * Get all control values (values of controls hidden by conditional logic are not included)
         */
        getValues: () => {
            if (!values) {
                /** Cache result to prevent re-evaluation  */
                values = getValues(state);
            }
            return values;
        },
        /**
         * Get all control values as FormData
         */
        getFormData: () => createSubmission(getValues(state)),
        /**
         * Check if a control value has been set
         * @param id
         */
        isDirty: (name: string) =>
            Object.prototype.hasOwnProperty.call(state.values, name)
    };
};

/**
 * Create value setter behaviours
 * @param state
 */
const canSetValues = (state: FormDispatchers) => ({
    /**
     * Set control value by id
     * @param id
     * @param value
     */
    setValue: (name: string, value: any) => {
        state.dispatchAction({
            type: ValueActionType.Set,
            body: { name, value }
        });
    },
    /**
     * Unset control value by id
     * @param id
     */
    unsetValue: (name: string) => {
        state.dispatchAction({ type: ValueActionType.Unset, body: name });
    },
    /**
     * Assign multiple values
     * @param values
     */
    assignValues: (values: FormValues) => {
        state.dispatchAction({ type: ValueActionType.Assign, body: values });
    }
});

/**
 * Create validation behaviours
 * @param state
 */
const validationBehaviours = (state: FormState) => {
    return Object.assign({}, canValidate(state));
};

const canValidate = (state: FormState) => ({
    validate: (overrideControls?: React.ReactNode[]) =>
        validate(state.rules, overrideControls || state.controls, state)
});

const validator = (
    state: FormState
) => validate(state.rules, visiblePageControls(state.pagination, state), state);

/**
 * Create paging behaviours
 * @param state
 */
const pagingBehaviours = (state: FormState & FormDispatchers) => {
    return Object.assign(
        {},
        canPaginate(state),
        canNavigate(state),
        canValidatePages(state)
    );
};

/**
 * Test is a component is a page
 * @param node
 */
const isPage = (node: React.ReactNode) =>
    React.isValidElement(node) && node.type === Page;

/**
 * Sort components into pages
 * @param components
 */
const paginateComponents = (
    components: PaginatedElement[]
): PaginatedComponents => {
    const firstChild = React.Children.toArray(components)[0];
    const pages: PaginatedComponents = [[], []];
    let length = isPage(firstChild) ? 0 : 1;
    React.Children.forEach(components, node => {
        if (isPage(node)) {
            length++;
            pages[length] = [];
        }
        pages[length].push(node);
    });
    return pages;
};

/**
 * Get props of a page
 * @param pagination
 * @param page
 */
const getPageProps = (pagination: PaginatedComponents, page: number) => {
    /**
     * Due to the way Gravity Form page breaks work the next & previous button properties come from the next page. All other properties apply to
     * the current page.
     */
    const pageInt = parseInt(`${page}`);
    const {
        conditionalLogic = null,
        cssClass = "",
        hideNext = false,
        hideBack = false,
        isFormModuleField = null,
        parentFormModuleId = null,
        ...remaining
    } = _getPageProps(pagination, pageInt);
    const {
        nextButton = { text: "Next" },
        previousButton = { text: "Back" }
    } = _getPageProps(pagination, pageInt + 1);

    return {
        ...remaining,
        nextButton,
        previousButton,
        hideNext,
        hideBack,
        conditionalLogic,
        cssClass,
        isFormModuleField,
        parentFormModuleId,
        children: pagination[page] && pagination[page].slice(0)
    };
};

const _getPageProps = (pagination: PaginatedComponents, page: number) => {
    const maybePage = {
        ...((pagination[page] || [])[0] || { props: {} }).props
    };
    return maybePage.type === "page" ? maybePage : ({} as PaginatedElement);
};

/**
 * Find the index of the page after the specified index
 * @param pagination
 * @param state
 */
const pageAfter = (state: FormState, index: number) =>
    Math.max(
        state.pagination.reduce((next: number, _, i) => {
            const page = getPageProps(state.pagination, i);
            if (
                !next &&
                i > index &&
                evaluates_as_visibleV2(page.conditionalLogic, state) &&
                !checkFormModuleConditionalLogic(page, state, false)
            ) {
                next = i;
            }
            return next;
        }, 0),
        index
    );

/**
 * Find the index of the page before the specified index
 * @param pagination
 * @param values
 * @param index
 */
const pageBefore = (state: FormState, index: number) =>
    Math.max(
        state.pagination.reduce((next: number, _, k) => {
            const i = state.pagination.length - 1 - k;
            const page = getPageProps(state.pagination, i);

            if (
                !next &&
                i < index &&
                evaluates_as_visibleV2(page.conditionalLogic, state) &&
                !checkFormModuleConditionalLogic(page, state, false)
            ) {
                next = i;
            }
            return next;
        }, 0),
        1
    );

const getNextConditionalLogic = (page: any) =>
    page.nextButton && page.nextButton.conditionalLogic;

const isNextDisabled = (state: FormState, page: number, skipValidation?: boolean) => {
    const p = getPageProps(state.pagination, page);
    const e = state.pagination[page].some(field =>
        state.validation[field.props.name] !== undefined
    );
    const h = evaluates_as_hiddenV2(getNextConditionalLogic(p), state);
    return (e && !skipValidation) || h;
};

const pageMaxBreak = (
    disabled: boolean,
    next: NodeBuilderFlags,
    last: number
) => disabled || next === last;

/**
 * Return the index of the maxiumu page that can be navigated to without errors
 * @param state
 * @returns
 */
export const pageMax = (state: FormState, skipValidation?: boolean) => {
    const last = lastPageIndex(state.pagination, state);
    let next = state.page,
        isBlocked = pageMaxBreak(isNextDisabled(state, next, skipValidation), next, last);
    while (!isBlocked) {
        next = pageAfter(state, next);
        isBlocked = pageMaxBreak(isNextDisabled(state, next, skipValidation), next, last);
    }
    return next;
};

/**
 * Find the last page index
 * @param pagination
 * @param values
 */
const lastPageIndex = (pagination: PaginatedComponents, state: FormState) => {
    let current = pagination.length - 1;
    let last = undefined;
    while (last === undefined) {
        const page = getPageProps(pagination, current);
        if (evaluates_as_visibleV2(page.conditionalLogic, state)) {
            last = current;
        } else {
            current -= 1;
        }
    }
    return last;
};

/**
 * Filter components on hidden pages
 * @param pagination
 * @param values
 */
const visiblePageControls = (
    pagination: PaginatedComponents,
    state: FormState
) =>
    pagination.reduce((visible: React.ReactNode[], components, i) => {
        const page = getPageProps(pagination, i);
        if (evaluates_as_visibleV2(page.conditionalLogic, state)) {
            visible = visible.concat(components);
        }
        return visible;
    }, []);

/**
 * Create paginate behaviours
 * @param state
 */
const canPaginate = (state: FormState) => {
    return {
        paginateComponents: (components?: PaginatedElement[]) =>
            components ? paginateComponents(components) : state.pagination
    };
};

/**
 * Create navigation behaviours
 * @param state
 */
const canNavigate = (state: FormState & FormDispatchers) => {
    return {
        getPage: (page?: number) =>
            getPageProps(state.pagination, page || state.page),
        pageAfter: (page?: number) => pageAfter(state, page || state.page),
        pageBefore: (page?: number) => pageBefore(state, page || state.page),
        gotoPage: (page: number) =>
            state.dispatchAction({
                type: PageActionType.GotoPage,
                body: page
            }),
        gotoNextPage: () =>
            state.dispatchAction({
                type: PageActionType.GotoNext,
                body: {}
            }),
        gotoLastPage: (skipValidation?: boolean) =>
            state.dispatchAction({
                type: PageActionType.GotoPage,
                body: pageMax(state, skipValidation)
            }),
        gotoPreviousPage: () =>
            state.dispatchAction({
                type: PageActionType.GotoPrevious,
                body: {}
            }),
        pageIsLast: (page?: number) =>
            lastPageIndex(state.pagination, state) === (page || state.page),
        isNextDisabled: (page?: number) =>
            isNextDisabled(state, page || state.page)
    };
};

/**
 * Create page validation behaviours
 * @param form
 * @param state
 */
const canValidatePages = (state: FormState) => ({
    validatePage: (overridePage?: number) => {
        return validate(
            state.rules,
            state.pagination[overridePage || state.page],
            state
        );
    },
    isPageHidden: (i?: number) =>
        evaluates_as_hiddenV2(
            getPageProps(state.pagination, parseInt(`${i}`) || state.page)
                .conditionalLogic,
            state
        )
});

const checkFormModuleConditionalLogic = (control: any, state: FormState, checkIfVisible: boolean) => {
    let hiddenFormModule = checkIfVisible;
    if (control.isFormModuleField === true) {
        const parentFormModuleControl = (state.controls as React.ReactElement[]).find(targetControl =>
            targetControl.props.type === "form_module" && targetControl.props.fieldId === control.parentFormModuleId);
        checkIfVisible === false
            ? hiddenFormModule = !!parentFormModuleControl && evaluates_as_hiddenV2(parentFormModuleControl.props.conditionalLogic, state)
            : hiddenFormModule = !!parentFormModuleControl && evaluates_as_visibleV2(parentFormModuleControl.props.conditionalLogic, state)
    }

    return hiddenFormModule;
}

export default createFormContext;
export {
    getPageProps,
    paginateComponents,
    pageAfter,
    pageBefore,
    getControlProps,
    validator,
    lastPageIndex
};
